import React, { FC, useEffect, useState } from "react";
import styled from "styled-components";
import CustomizeContent from "./components/CustomizeContent";
import CookieConsentSettings from "./components/CookieConsentSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faToggleOn } from "@fortawesome/pro-light-svg-icons";

type OneCookieProp = {
    title: string;
    description: string;
    expiry: string;
    type: string;
};

export type ProviderProps = {
    title: string;
    link: string;
    items: OneCookieProp[];
};

export type CookieCategoryProp = {
    id: string;
    title: string;
    description?: string;
    required: boolean;
    providers: ProviderProps[];
};

type PropsByDominikFeatMilos = {
    title: string;
    content: string; // rich
    buttons: {
        allow: string;
        decline: string;
        customize: string;
        selected: string;
        aboutProvider: string;
        change: string;
    };
    settings: {
        date: string;
        acceptID: string;
        title: string;
        expiry: string;
        type: string;
    };
    info: string;
    categories: CookieCategoryProp[];
    blockingMode?: boolean;
    expiration?: number; // in days
};

export type CookieConsentProps = PropsByDominikFeatMilos;

export interface ToggleStatesProps {
    [index: string]: "denied" | "granted";
}

interface ButtonStyleProps {
    backgroundColor: string;
    textColor: string;
    hoverBackgroundColor: string;
    hoverTextColor: string;
}

const Container = styled.div<{ linkColor: string }>`
	z-index: 99999999;
	width: 456px;
	background-color: #ffffff;
	font-family: "Poppins", sans-serif;
	border-radius: 8px;
	position: fixed;
	bottom: 24px;
	left: 24px;
	max-height: calc(100% - 48px);
	overflow: auto;
	padding: 48px 0 32px 0;
	box-shadow: 0 4px 16px 0 #0000001a;

	&::-webkit-scrollbar {
		width: 0;
	}

	& a {
		color: ${({ linkColor }) => linkColor};
		font-weight: 600;
		text-decoration: none;
	}

	@media screen and (max-width: 500px) {
		width: 90%;
	}
`;

const Title = styled.h6`
	color: #323330;
	font-weight: 700;
	font-family: var(--branding-font), sans-serif;
	font-size: 32px;
	line-height: 38px;
	margin: 0;
	padding: 0 48px;
	text-align: center;
	@media screen and (max-width: 500px) {
		padding: 0 24px;
	}
	@media screen and (max-width: 440px) {
		font-size: 22px;
	}
`;

const Content = styled.div<{ customizeActive: boolean }>`
	text-align: center;
	margin: ${({ customizeActive }) => (customizeActive ? "0 0 24px 0" : "32px 0 24px 0")};
	color: #626560;
	font-size: 14px;
	line-height: 24px;
	font-weight: 400;
	padding: 0 48px;
	max-height: ${({ customizeActive }) => (customizeActive ? "0px" : "600px")};
	overflow: hidden;
	@media screen and (max-width: 500px) {
		padding: 0 24px;
	}
`;

const Button = styled.button<ButtonStyleProps>`
	border-radius: 8px;
	padding: 16px 24px;
	font-size: 18px;
	line-height: 21px;
	cursor: pointer;
	font-family: var(--branding-font), sans-serif;
	font-weight: 600;
	text-align: center;
	border: none;
	background-color: ${(props) => props.backgroundColor};
	color: ${(props) => props.textColor};
	display: block;
	width: calc(100% - 96px);
	margin: 8px 48px 0 48px;
	outline: none;
	transition: 0.25s background-color, 0.25s color;

	&:hover {
		background-color: ${(props) => props.hoverBackgroundColor};
		color: ${(props) => props.hoverTextColor};
	}

	@media screen and (max-width: 500px) {
		margin: 8px 24px 0 24px;
		width: calc(100% - 48px);
	}
`;

const Info = styled.div<{ customizeActive: boolean }>`
	font-weight: 400;
	color: #626560;
	font-size: 12px;
	line-height: 20px;
	text-align: center;
	margin-top: ${({ customizeActive }) => (customizeActive ? "0px" : "8px")};
	padding: 0 48px;
	max-height: ${({ customizeActive }) => (customizeActive ? "0px" : "500px")};
	overflow: hidden;
	@media screen and (max-width: 500px) {
		padding: 0 24px;
	}
`;

const Circle = styled.div`
	z-index: 99;
	width: 48px;
	height: 48px;
	background-color: #ffffff;
	border-radius: 50%;
	box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
	position: fixed;
	bottom: 24px;
	left: 24px;
	font-size: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
`;

const CookieIcon = styled.span`
	font-weight: 400;
`;

const OtherButtonSection = styled.div<{ activeCustomization: boolean; }>`
  display: grid;
  grid-template-columns: repeat(${({activeCustomization}) => activeCustomization ? 1 : 2}, 1fr);
  grid-column-gap: 16px;
  width: calc(100% - 96px);
  margin: ${({activeCustomization}) => activeCustomization ? 0 : "32px"} 48px 0 48px;

  @media screen and (max-width: 500px) {
    margin: ${({activeCustomization}) => activeCustomization ? 0 : "24px"} 24px 0 24px;
    width: calc(100% - 48px);
  }
`;

const SmallButton = styled.button<ButtonStyleProps>`
	border-radius: 8px;
	padding: 16px 24px;
	font-size: 18px;
	line-height: 21px;
	cursor: pointer;
	font-family: var(--branding-font), sans-serif;
	font-weight: 600;
	text-align: center;
	border: none;
	background-color: ${(props) => props.backgroundColor};
	color: ${(props) => props.textColor};
	display: block;
	width: 100%;
	outline: none;
	transition: 0.25s background-color, 0.25s color;

	&:hover {
		background-color: ${(props) => props.hoverBackgroundColor};
		color: ${(props) => props.hoverTextColor};
	}

	@media screen and (max-width: 500px) {
		width: 100%;
	}
`;

type SectionType = 1 | 2 | 3;

// const fake_token = "fake_token_cc";
const CookieConsent: FC<CookieConsentProps> = ({ buttons, categories, content, info, settings, title, expiration }) => {
    const [showSection, setShowSection] = useState<SectionType>(1);
    const [customizeActive, setCustomizeActive] = useState<boolean>(false);
    const [date, setDate] = useState<Date>(new Date());
    const [toggleStates, setToggleStates] = useState<ToggleStatesProps>(
        (function () {
            const temp: ToggleStatesProps = {};
            categories.forEach((c) => {
                temp[c.id] = c.required ? "granted" : "denied";
            });
            return temp;
        })()
    );

    const [token, setToken] = useState<string | null>(null);

    function gtag(){(window as any).dataLayer.push(arguments);}

    const getToken = async (): Promise<string | null> => {
        // localStorage.setItem("cc_token", fake_token);
        // return fake_token;

        (window as any).dataLayer = (window as any).dataLayer || [];

        console.log("COOKIES", "getting token from server");

        const response: any = await fetch("/api/Cookies/Token/" + (localStorage.getItem("cc_token") ?? ""));

        console.log("COOKIES", "got response from server");

        const temp: ToggleStatesProps = {};
        categories.forEach((c) => {
            temp[c.id] = "denied";
        });

        if (response.status === 200) {
            console.log("reading response", response, response.status)
            const obj = await response.json();
            localStorage.setItem("cc_token", obj.token);
            console.log("COOKIES", "token is:", obj.token);

            const x = new Date(obj.updatedOn);
            const y = new Date();
            y.setHours(y.getHours() - (expiration ?? 365)*24);

            console.log(y, x, x < y, x > y);

            if(x >= y && obj.selection != null) {
                localStorage.setItem(
                    "cc_settings",
                    JSON.stringify({
                        ...obj.selection,
                        date: obj.updatedOn,
                    })
                );

                setDate(obj.updatedOn);

                const final: ToggleStatesProps = {};
                categories.forEach((c) => {
                    if(obj.selection != null && obj.selection[c.id]) final[c.id] = obj.selection[c.id] ? "granted" : "denied"
                    else final[c.id] = "denied";
                });
                setToggleStates(final);

                // @ts-ignore
                gtag('consent', 'update', final);
                (window as any).dataLayer.push({ event: 'consent_updated' });
            }
            else {
                localStorage.removeItem("cc_settings");
                setShowSection(3);
            }

            return obj.token;
        }
        else {
            localStorage.removeItem("cc_settings");
            setShowSection(3);
        }
        console.log("COOKIES", "response was not succesful, no token");

        return null;
    };

    const submitSelection = async (values: ToggleStatesProps): Promise<void> => {
        // return;

        (window as any).dataLayer = (window as any).dataLayer || [];

        const final: any = {};
        Object.keys(values).forEach((c) => {
            if(values[c] == "granted") final[c] = true
            else final[c] = false;
        });

        const response = await fetch("/api/Cookies/" + token, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(final),
        });

        if(response.status !== 200) {
            localStorage.removeItem("cc_settings");
            localStorage.removeItem("cc_token");
        }

        setToggleStates(values);

        // @ts-ignore
        gtag('consent', 'update', values);
        (window as any).dataLayer.push({ event: 'consent_updated' });
    };

    useEffect(() => { getToken().then((e) => setToken(e)); }, []);

    const onToggleChange = (key: string): void => {
        setToggleStates((prev) => ({
            ...prev,
            [key]: prev[key] === "granted" ? "denied" : "granted",
        }));
    };

    const acceptAll = (): void => {
        const values: ToggleStatesProps = {};
        Object.keys(toggleStates).forEach((ts) => {
            values[ts] = "granted";
        });
        localStorage.setItem(
            "cc_settings",
            JSON.stringify({
                ...values,
                date: new Date(),
            })
        );
        submitSelection(values).then();
        setShowSection(1);
        setCustomizeActive(false);
    };

    const denyAll = (): void => {
        const states: ToggleStatesProps = {};
        Object.keys(toggleStates).forEach((ts) => {
            states[ts] = "denied";
        });

        categories.forEach(_ => {
           if(states[_.id] == "denied" && _.required) {
               states[_.id] = "granted"
           }
        });

        localStorage.setItem(
            "cc_settings",
            JSON.stringify({
                ...states,
                date: new Date(),
            })
        );
        submitSelection(states).then();
        setShowSection(1);
        setCustomizeActive(false);
    };

    const acceptSelected = (): void => {
        localStorage.setItem(
            "cc_settings",
            JSON.stringify({
                ...toggleStates,
                date: new Date(),
            })
        );
        submitSelection(toggleStates).then();
        setShowSection(1);
        setCustomizeActive(false);
    };

    const onClose = (): void => {
        setShowSection(1);
    };

    const objectMap = (obj: object, fn: (v: any, k?: any, i?: any) => any) => Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));

    console.log("COOKIES", token, showSection);

    return (
        <>
            {token !== null && (
                <>
                    {showSection === 1 && (
                        <Circle onClick={() => setShowSection(2)}>
                            <FontAwesomeIcon icon={faToggleOn} />
                        </Circle>
                    )}
                    {showSection === 2 && (
                        <CookieConsentSettings
                            date={{ date: date.toLocaleString(), title: settings.date }}
                            id={{ id: token, title: settings.acceptID }}
                            onChange={() => setShowSection(3)}
                            onClose={onClose}
                            changeBtn={buttons.change}
                            title={settings.title}
                            toggles={objectMap(toggleStates, (value, key, index) => {
                                const cat = categories.find((c) => c.id === key)!;
                                return {
                                    title: cat.title,
                                    state: value,
                                    required: cat.required,
                                };
                            })}
                        />
                    )}
                    {showSection === 3 && (
                        <Container linkColor={"#7cc242"}>
                            <Title>{title}</Title>
                            <Content
                                customizeActive={customizeActive}
                                dangerouslySetInnerHTML={{
                                    __html: content,
                                }}
                            />
                            <Button onClick={acceptAll} backgroundColor={"#323330"} hoverBackgroundColor={"#2b2b29"} hoverTextColor={"white"} textColor={"white"}>
                                {buttons.allow}
                            </Button>
                            <CustomizeContent settings={{ expiry: settings.expiry, type: settings.type }} active={customizeActive} onToggleChange={onToggleChange} toggleStates={toggleStates} info={info} aboutProvider={buttons.aboutProvider} infoProps={categories} />
                            <Info
                                customizeActive={customizeActive}
                                dangerouslySetInnerHTML={{
                                    __html: info,
                                }}
                            />
                            <OtherButtonSection activeCustomization={customizeActive}>
                                {!customizeActive && <SmallButton
                                    backgroundColor={"#F6F7F6"}
                                    hoverBackgroundColor={"#e9eae9"}
                                    hoverTextColor={"#323330"}
                                    textColor={"#323330"}
                                    onClick={denyAll}
                                >
                                    {buttons.decline}
                                </SmallButton>}
                                <SmallButton
                                    backgroundColor={"#F6F7F6"}
                                    hoverBackgroundColor={"#e9eae9"}
                                    hoverTextColor={"#323330"}
                                    textColor={"#323330"}
                                    onClick={() => (!customizeActive ? setCustomizeActive(true) : acceptSelected())}
                                >
                                    {customizeActive ? buttons.selected : buttons.customize}
                                </SmallButton>
                            </OtherButtonSection>
                        </Container>
                    )}
                </>
            )}
        </>
    );
};

export default CookieConsent;